import { Box, useTheme } from '@mui/material';
import {
  differenceInCalendarDays,
  endOfDay,
  format,
  formatDistanceStrict,
  parseISO,
} from 'date-fns';
import {
  ClipboardCheck,
  DoorOpen,
  type LucideIcon,
  Phone,
  TriangleAlert,
  UserCheck,
} from 'lucide-react';

import { type Translate, useLocale } from '@shared/hooks/locale';

type BuyerLeadNextActivity = {
  activity_type: string;
  due_at?: string | null;
  start_date?: string | null;
};

export const getBuyerLeadNextStep = (
  activity: BuyerLeadNextActivity | null | undefined,
  now: Date,
) => {
  if (activity == null) {
    return null;
  }

  const allDay = activity.due_at == null && activity.start_date != null;
  const date =
    activity.due_at != null
      ? parseISO(activity.due_at)
      : activity.start_date != null
      ? endOfDay(parseISO(activity.start_date))
      : null;

  return {
    type: activity.activity_type,
    date,
    allDay,
    late: date != null && date < now,
  };
};

const describeType = (
  type: string,
  t: Translate,
): { Icon: LucideIcon; label: string } => {
  switch (type) {
    case 'call':
      return { Icon: Phone, label: t('Call') };
    case 'visit':
      return { Icon: DoorOpen, label: t('Visit') };
    case 'assignment':
      return { Icon: UserCheck, label: t('Assignment') };
    default:
      return { Icon: ClipboardCheck, label: t('Task') };
  }
};

export const BuyerLeadNextStep: React.FC<{
  activity: BuyerLeadNextActivity | null | undefined;
}> = ({ activity }) => {
  const { t, dateLocale } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const now = new Date();
  const step = getBuyerLeadNextStep(activity, now);

  const { Icon, label } =
    step == null
      ? { Icon: TriangleAlert, label: t('No next step') }
      : describeType(step.type, t);

  const color =
    step == null
      ? palette.error.main
      : step.late
      ? palette.warning.main
      : palette.info.main;

  const when =
    step?.date == null
      ? null
      : step.late
      ? t('{{time}} late', {
          time: formatDistanceStrict(step.date, now, { locale: dateLocale }),
        })
      : format(
          step.date,
          differenceInCalendarDays(step.date, now) < 7
            ? step.allDay
              ? 'EEE'
              : 'EEE HH:mm'
            : 'd MMM',
          { locale: dateLocale },
        );

  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        mt: 0.5,
        minWidth: 0,
        fontSize: '0.75rem',
        color,
      }}
    >
      <Icon size={14} style={{ flexShrink: 0 }} />
      <Box
        component="span"
        sx={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {when == null ? label : `${label} · ${when}`}
      </Box>
    </Box>
  );
};
